import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import { formatDate, calculateAge } from '../utils/AuthUtils';
import Pagination from './Pagination';
import { printDocument } from '../utils/printDocument';
import {
  FileText, Plus, Search, Trash2, X, Save, Printer, FileCheck, Edit
} from 'lucide-react';

const MedicalCertificates = ({ currentBranch, user }) => {
  // Result of the last print: `error` means nothing printed, `warning` means
  // it printed through the browser when a printer was configured.
  const [printWarning, setPrintWarning] = useState('');
  const [printError, setPrintError] = useState('');
  const { print_lang } = useLanguage();
  const printRef = useRef(null);

  // Data states
  const [certificates, setCertificates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [treatmentRecords, setTreatmentRecords] = useState([]);

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedCert, setSelectedCert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Form data
  const [formData, setFormData] = useState({
    patient_id: '',
    dentist_id: '',
    treatment_record_id: '',
    template_type: 'standard',
    diagnosis: '',
    recommendation: '',
    rest_days: 0,
    rest_start_date: '',
    rest_end_date: '',
    issue_date: new Date().toISOString().split('T')[0]
  });

  // Template types
  const templateTypes = [
    { id: 'standard', name: 'ใบรับรองแพทย์มาตรฐาน', name_en: 'Standard Medical Certificate' },
    { id: 'dental_council', name: 'แบบทันตแพทยสภา', name_en: 'Dental Council Format' },
    { id: 'sick_leave', name: 'ใบรับรองลาป่วย', name_en: 'Sick Leave Certificate' },
    { id: 'fitness', name: 'ใบรับรองสุขภาพ', name_en: 'Health/Fitness Certificate' }
  ];

  // Common diagnoses for quick selection
  const commonDiagnoses = [
    'ฟันผุ (Dental Caries)',
    'เหงือกอักเสบ (Gingivitis)',
    'ปริทันต์อักเสบ (Periodontitis)',
    'ฟันคุด (Impacted Tooth)',
    'ฟันร้าว/แตก (Cracked/Fractured Tooth)',
    'รากฟันอักเสบ (Pulpitis)',
    'ฝีหนองในช่องปาก (Dental Abscess)',
    'เยื่อบุช่องปากอักเสบ (Stomatitis)'
  ];

  // Common recommendations
  const commonRecommendations = [
    'รับประทานยาตามที่แพทย์สั่งจนหมด',
    'หลีกเลี่ยงอาหารร้อนและแข็งเป็นเวลา 24 ชั่วโมง',
    'บ้วนปากด้วยน้ำเกลืออุ่นวันละ 2-3 ครั้ง',
    'พักผ่อนให้เพียงพอ',
    'มาพบแพทย์ตามนัดหมาย',
    'หากมีอาการผิดปกติให้มาพบแพทย์ทันที'
  ];

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchCertificates();
      fetchPatients();
      fetchDentists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch]);

  const fetchCertificates = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`/api/certificates?branch_id=${currentBranch.branch_id}`);
      setCertificates(data.data || []);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const data = await fetchWithAuth(`/api/patients?branch_id=${currentBranch.branch_id}&status=active&limit=100`);
      setPatients(data.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const fetchDentists = async () => {
    try {
      const data = await fetchWithAuth(`/api/appointments/dentists/selection?branch_id=${currentBranch.branch_id}`);
      setDentists(data.data || data || []);
    } catch (error) {
      console.error('Failed to fetch dentists:', error);
    }
  };

  const fetchPatientTreatments = async (patientId) => {
    try {
      const data = await fetchWithAuth(`/api/treatment-records/patient/${patientId}?limit=10`);
      setTreatmentRecords(data.data || []);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
      setTreatmentRecords([]);
    }
  };

  const handleAdd = () => {
    setSelectedCert(null);
    setFormData({
      patient_id: '',
      dentist_id: user?.dentist_id || '',
      treatment_record_id: '',
      template_type: 'standard',
      diagnosis: '',
      recommendation: '',
      rest_days: 0,
      rest_start_date: new Date().toISOString().split('T')[0],
      rest_end_date: '',
      issue_date: new Date().toISOString().split('T')[0]
    });
    setTreatmentRecords([]);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEdit = async (cert) => {
    try {
      const data = await fetchWithAuth(`/api/certificates/${cert.cert_id}`);
      const certData = data.data;
      setSelectedCert(certData);
      setFormData({
        patient_id: certData.patient_id || '',
        dentist_id: certData.dentist_id || '',
        treatment_record_id: certData.treatment_record_id || '',
        template_type: certData.template_type || 'standard',
        diagnosis: certData.diagnosis || '',
        recommendation: certData.recommendation || '',
        rest_days: certData.rest_days || 0,
        rest_start_date: certData.rest_start_date ? certData.rest_start_date.split('T')[0] : '',
        rest_end_date: certData.rest_end_date ? certData.rest_end_date.split('T')[0] : '',
        issue_date: certData.issue_date ? certData.issue_date.split('T')[0] : new Date().toISOString().split('T')[0]
      });
      if (certData.patient_id) {
        fetchPatientTreatments(certData.patient_id);
      }
      setModalMode('edit');
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
    }
  };

  const handleView = async (cert) => {
    try {
      const data = await fetchWithAuth(`/api/certificates/${cert.cert_id}`);
      setSelectedCert(data.data);
      setShowPrintModal(true);
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
    }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm(print_lang('confirm_delete_cert', 'Are you sure you want to delete this certificate?'))) {
      return;
    }

    try {
      await fetchWithAuth(`/api/certificates/${certId}`, { method: 'DELETE' });
      fetchCertificates();
    } catch (error) {
      console.error('Failed to delete certificate:', error);
      alert(error.message);
    }
  };

  const handlePatientChange = (patientId) => {
    setFormData({ ...formData, patient_id: patientId, treatment_record_id: '' });
    if (patientId) {
      fetchPatientTreatments(patientId);
    } else {
      setTreatmentRecords([]);
    }
  };

  const handleTreatmentSelect = (treatmentId) => {
    const treatment = treatmentRecords.find(t => t.tmr_id === parseInt(treatmentId));
    if (treatment) {
      setFormData({
        ...formData,
        treatment_record_id: treatmentId,
        diagnosis: treatment.diagnosis || formData.diagnosis,
        dentist_id: treatment.dentist_id || formData.dentist_id
      });
    }
  };

  const handleRestDaysChange = (days) => {
    const startDate = formData.rest_start_date || new Date().toISOString().split('T')[0];
    let endDate = '';

    if (days > 0 && startDate) {
      const end = new Date(startDate);
      end.setDate(end.getDate() + parseInt(days) - 1);
      endDate = end.toISOString().split('T')[0];
    }

    setFormData({
      ...formData,
      rest_days: days,
      rest_start_date: startDate,
      rest_end_date: endDate
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.patient_id || !formData.dentist_id) {
      alert(print_lang('patient_dentist_required', 'Patient and dentist are required'));
      return;
    }

    setIsSaving(true);
    try {
      if (modalMode === 'edit' && selectedCert) {
        // Update existing certificate
        await fetchWithAuth(`/api/certificates/${selectedCert.cert_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            diagnosis: formData.diagnosis,
            recommendation: formData.recommendation,
            rest_days: formData.rest_days,
            rest_start_date: formData.rest_start_date,
            rest_end_date: formData.rest_end_date
          })
        });
      } else {
        // Create new certificate
        await fetchWithAuth('/api/certificates', {
          method: 'POST',
          body: JSON.stringify({
            ...formData,
            branch_id: currentBranch.branch_id
          })
        });
      }

      setShowModal(false);
      fetchCertificates();
    } catch (error) {
      console.error('Failed to save certificate:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Certificates are A4, so they route to the branch's `document` printer and
  // fall back to the browser dialog when the agent is not running.
  //
  // The agent's copy carries no auto-print script — the agent prints the
  // document itself, and a window.print() inside it would fire a second print
  // in its hidden window.
  const AUTO_PRINT_SCRIPT = `
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 500);
          };
        </script>`;

  const buildCertificateHtml = (autoPrint) => {
    const printContent = printRef.current;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ใบรับรองแพทย์ - ${selectedCert?.cert_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Sarabun', 'TH Sarabun New', sans-serif;
            font-size: 15px;
            line-height: 1.5;
            color: #000;
            background: #fff;
          }

          .certificate {
            width: 210mm;
            height: 297mm;
            padding: 8mm 15mm;
            margin: 0 auto;
            background: #fff;
            overflow: hidden;
          }

          .header {
            text-align: center;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 2px double #000;
          }

          .header .logo {
            height: 50px;
            margin-bottom: 5px;
          }

          .header h1 {
            font-size: 22px;
            font-weight: bold;
            margin-bottom: 3px;
            letter-spacing: 1px;
          }

          .header h2 {
            font-size: 20px;
            font-weight: bold;
            margin: 6px 0;
            padding: 5px 0;
            border-top: 1px solid #000;
            border-bottom: 1px solid #000;
          }

          .header .clinic-info {
            font-size: 12px;
            color: #333;
            margin-top: 5px;
          }

          .cert-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 14px;
          }

          .content {
            margin: 12px 0;
          }

          .form-row {
            display: flex;
            margin-bottom: 8px;
            align-items: baseline;
          }

          .form-label {
            font-weight: 600;
            min-width: 180px;
            flex-shrink: 0;
            font-size: 14px;
          }

          .form-value {
            flex: 1;
            border-bottom: 1px dotted #333;
            padding: 0 8px 1px 8px;
            min-height: 20px;
            font-size: 14px;
          }

          .section-box {
            border: 1px solid #333;
            padding: 8px 12px;
            margin: 10px 0;
            min-height: 60px;
          }

          .section-box h4 {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
            padding-bottom: 5px;
            border-bottom: 1px solid #ddd;
            background: #f5f5f5;
            margin: -8px -12px 8px -12px;
            padding: 6px 12px;
          }

          .section-box p {
            white-space: pre-line;
            font-size: 14px;
            line-height: 1.4;
          }

          .rest-section {
            background: #f9f9f9;
            border: 1px solid #333;
            padding: 10px;
            margin: 12px 0;
            text-align: center;
          }

          .rest-section .rest-title {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
          }

          .rest-section .rest-days {
            font-size: 28px;
            font-weight: bold;
            color: #c00;
            margin: 5px 0;
          }

          .rest-section .rest-dates {
            font-size: 14px;
            margin-top: 5px;
          }

          .signature-section {
            margin-top: 25px;
            display: flex;
            justify-content: space-between;
            padding: 0 20px;
          }

          .signature-box {
            width: 42%;
            text-align: center;
          }

          .signature-space {
            height: 55px;
            position: relative;
          }

          .signature-img {
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            height: 50px;
          }

          .signature-line {
            border-top: 1px solid #000;
            padding-top: 5px;
            font-size: 13px;
            line-height: 1.4;
          }

          .stamp-box {
            width: 42%;
            text-align: center;
          }

          .stamp-space {
            height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .stamp-img {
            max-height: 55px;
            opacity: 0.8;
          }

          .footer {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            text-align: center;
            font-size: 12px;
            color: #666;
          }

          .footer p {
            margin: 2px 0;
          }

          @media print {
            @page {
              size: A4;
              margin: 0;
            }

            html, body {
              width: 210mm;
              height: 297mm;
            }

            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate {
              width: 210mm;
              height: 297mm;
              padding: 8mm 15mm;
              page-break-after: avoid;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${printContent ? printContent.innerHTML : ''}${autoPrint ? AUTO_PRINT_SCRIPT : ''}
      </body>
      </html>
    `;
  };

  const handlePrint = async () => {
    setPrintWarning('');
    setPrintError('');
    const result = await printDocument({
      agentHtml: buildCertificateHtml(false),
      browserHtml: buildCertificateHtml(true),
      printerType: 'document',
      branch: currentBranch,
    });
    if (result.popupBlocked) {
      setPrintError(print_lang('popup_blocked', 'Popup blocked — allow popups and try again'));
      return;
    }
    if (result.warned) {
      setPrintWarning(print_lang('printed_via_browser', 'Print agent offline — printed via the browser'));
    }
  };

  const getPatientAge = (dob) => {
    if (!dob) return '-';
    return calculateAge(dob);
  };

  const formatThaiDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    const day = date.getDate();
    const month = thaiMonths[date.getMonth()];
    const year = date.getFullYear() + 543; // Buddhist year
    return `${day} ${month} ${year}`;
  };

  const filteredCertificates = certificates.filter(cert => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      cert.cert_number?.toLowerCase().includes(search) ||
      cert.patient_name?.toLowerCase().includes(search) ||
      cert.dentist_name?.toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalCount = filteredCertificates.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedCertificates = filteredCertificates.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const renderCertificateContent = () => {
    if (!selectedCert) return null;

    const cert = selectedCert;

    // Inline styles for preview (matches print CSS)
    const styles = {
      certificate: {
        width: '210mm',
        height: '297mm',
        padding: '8mm 15mm',
        margin: '0 auto',
        background: '#fff',
        fontFamily: "'Sarabun', 'TH Sarabun New', sans-serif",
        fontSize: '15px',
        lineHeight: '1.5',
        color: '#000',
        overflow: 'hidden'
      },
      header: {
        textAlign: 'center',
        marginBottom: '12px',
        paddingBottom: '10px',
        borderBottom: '2px double #000'
      },
      logo: { height: '50px', marginBottom: '5px' },
      h1: { fontSize: '22px', fontWeight: 'bold', marginBottom: '3px', letterSpacing: '1px' },
      h2: { fontSize: '20px', fontWeight: 'bold', margin: '6px 0', padding: '5px 0', borderTop: '1px solid #000', borderBottom: '1px solid #000' },
      clinicInfo: { fontSize: '12px', color: '#333', marginTop: '5px' },
      certMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' },
      content: { margin: '12px 0' },
      formRow: { display: 'flex', marginBottom: '8px', alignItems: 'baseline' },
      formLabel: { fontWeight: '600', minWidth: '180px', flexShrink: 0, fontSize: '14px' },
      formValue: { flex: 1, borderBottom: '1px dotted #333', padding: '0 8px 1px 8px', minHeight: '20px', fontSize: '14px' },
      sectionBox: { border: '1px solid #333', padding: '8px 12px', margin: '10px 0', minHeight: '60px' },
      sectionH4: { fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', paddingBottom: '5px', borderBottom: '1px solid #ddd', background: '#f5f5f5', margin: '-8px -12px 8px -12px', padding: '6px 12px' },
      sectionP: { whiteSpace: 'pre-line', fontSize: '14px', lineHeight: '1.4' },
      restSection: { background: '#f9f9f9', border: '1px solid #333', padding: '10px', margin: '12px 0', textAlign: 'center' },
      restTitle: { fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' },
      restDays: { fontSize: '28px', fontWeight: 'bold', color: '#c00', margin: '5px 0' },
      restDates: { fontSize: '14px', marginTop: '5px' },
      signatureSection: { marginTop: '25px', display: 'flex', justifyContent: 'space-between', padding: '0 20px' },
      signatureBox: { width: '42%', textAlign: 'center' },
      signatureSpace: { height: '55px', position: 'relative' },
      signatureImg: { position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', height: '50px' },
      signatureLine: { borderTop: '1px solid #000', paddingTop: '5px', fontSize: '13px', lineHeight: '1.4' },
      stampSpace: { height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
      stampImg: { maxHeight: '55px', opacity: 0.8 },
      footer: { marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #ccc', textAlign: 'center', fontSize: '12px', color: '#666' }
    };

    return (
      <div className="certificate" ref={printRef} style={styles.certificate}>
        {/* Header */}
        <div className="header" style={styles.header}>
          {cert.logo_path && (
            <img
              src={cert.logo_path.startsWith('http') ? cert.logo_path : `${API_URL}/${cert.logo_path}`}
              alt="Logo"
              className="logo"
              style={styles.logo}
            />
          )}
          <h1 style={styles.h1}>{cert.branch_name}</h1>
          <h2 style={styles.h2}>ใบรับรองแพทย์ / MEDICAL CERTIFICATE</h2>
          <div className="clinic-info" style={styles.clinicInfo}>
            <p>{cert.branch_address}</p>
            <p>โทร: {cert.branch_phone} | เลขที่ใบอนุญาต: {cert.dental_license}</p>
          </div>
        </div>

        {/* Certificate Meta */}
        <div className="cert-meta" style={styles.certMeta}>
          <span>เลขที่ / No.: <strong>{cert.cert_number}</strong></span>
          <span>วันที่ออก / Date: <strong>{formatThaiDate(cert.issue_date)}</strong></span>
        </div>

        {/* Content */}
        <div className="content" style={styles.content}>
          {/* Dentist Info */}
          <div className="form-row" style={styles.formRow}>
            <span className="form-label" style={styles.formLabel}>ข้าพเจ้า / I,</span>
            <span className="form-value" style={styles.formValue}>{cert.dentist_name}</span>
          </div>
          <div className="form-row" style={styles.formRow}>
            <span className="form-label" style={styles.formLabel}>ใบอนุญาตฯ / License No.</span>
            <span className="form-value" style={styles.formValue}>{cert.license_number || '-'}</span>
          </div>

          {/* Patient Info */}
          <div className="form-row" style={{ ...styles.formRow, marginTop: '12px' }}>
            <span className="form-label" style={styles.formLabel}>ได้ทำการตรวจ / Examined</span>
            <span className="form-value" style={styles.formValue}>{cert.patient_name}</span>
          </div>
          <div className="form-row" style={styles.formRow}>
            <span className="form-label" style={styles.formLabel}>เลขประจำตัวประชาชน / ID No.</span>
            <span className="form-value" style={styles.formValue}>{cert.citizen_id || '-'}</span>
          </div>
          <div className="form-row" style={styles.formRow}>
            <span className="form-label" style={styles.formLabel}>อายุ / Age</span>
            <span className="form-value" style={styles.formValue}>{getPatientAge(cert.dob)} ปี</span>
          </div>
          <div className="form-row" style={styles.formRow}>
            <span className="form-label" style={styles.formLabel}>ที่อยู่ / Address</span>
            <span className="form-value" style={styles.formValue}>{cert.patient_address || '-'}</span>
          </div>
        </div>

        {/* Diagnosis */}
        <div className="section-box" style={styles.sectionBox}>
          <h4 style={styles.sectionH4}>ผลการตรวจ / Diagnosis</h4>
          <p style={styles.sectionP}>{cert.diagnosis || 'ตรวจไม่พบความผิดปกติ / No abnormality detected'}</p>
        </div>

        {/* Recommendation */}
        <div className="section-box" style={styles.sectionBox}>
          <h4 style={styles.sectionH4}>ความเห็นและข้อแนะนำ / Opinion & Recommendation</h4>
          <p style={styles.sectionP}>{cert.recommendation || '-'}</p>
        </div>

        {/* Rest Days */}
        {cert.rest_days > 0 && (
          <div className="rest-section" style={styles.restSection}>
            <div className="rest-title" style={styles.restTitle}>ควรหยุดพักรักษาตัว / Should Rest For</div>
            <div className="rest-days" style={styles.restDays}>{cert.rest_days} วัน / Days</div>
            <div className="rest-dates" style={styles.restDates}>
              ตั้งแต่วันที่ / From: <strong>{formatThaiDate(cert.rest_start_date)}</strong>
              {' '} ถึง / To: <strong>{formatThaiDate(cert.rest_end_date)}</strong>
            </div>
          </div>
        )}

        {/* Signature Section */}
        <div className="signature-section" style={styles.signatureSection}>
          <div className="signature-box" style={styles.signatureBox}>
            <div className="signature-space" style={styles.signatureSpace}>
              {cert.signature_path && (
                <img
                  src={cert.signature_path.startsWith('http') ? cert.signature_path : `${API_URL}/${cert.signature_path}`}
                  alt="Signature"
                  className="signature-img"
                  style={styles.signatureImg}
                />
              )}
            </div>
            <div className="signature-line" style={styles.signatureLine}>
              <strong>({cert.dentist_name})</strong>
              <br />
              ทันตแพทย์ผู้ตรวจ / Examining Dentist
              <br />
              ท.พ. {cert.license_number || '-'}
            </div>
          </div>

          <div className="stamp-box" style={styles.signatureBox}>
            <div className="stamp-space" style={styles.stampSpace}>
              {/* Empty space for physical clinic stamp */}
            </div>
            <div className="signature-line" style={styles.signatureLine}>
              ตราประทับคลินิก
              <br />
              Clinic Stamp
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer" style={styles.footer}>
          <p style={{ margin: '2px 0' }}>ใบรับรองแพทย์ฉบับนี้ออกให้เพื่อรับรองตามความเป็นจริง</p>
          <p style={{ margin: '2px 0' }}>This certificate is issued to certify the above statements are true.</p>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText size={28} />
          {print_lang('medical_certificate', 'Medical Certificate')}
        </h1>
        <button onClick={handleAdd} className="btn btn-primary flex items-center gap-2">
          <Plus size={20} />
          {print_lang('new_certificate', 'New Certificate')}
        </button>
      </div>

      {/* Search */}
      <div className="card mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={print_lang('search_certificate', 'Search by certificate number, patient, or dentist...')}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : paginatedCertificates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{print_lang('no_certificates', 'No certificates found')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>{print_lang('cert_number', 'Certificate No.')}</th>
                  <th>{print_lang('issue_date', 'Issue Date')}</th>
                  <th>{print_lang('patient', 'Patient')}</th>
                  <th>{print_lang('dentist', 'Dentist')}</th>
                  <th>{print_lang('rest_days', 'Rest Days')}</th>
                  <th>{print_lang('actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCertificates.map((cert) => (
                  <tr key={cert.cert_id}>
                    <td className="font-mono font-medium">{cert.cert_number}</td>
                    <td>{formatDate(cert.issue_date)}</td>
                    <td>{cert.patient_name}</td>
                    <td>{cert.dentist_name}</td>
                    <td>
                      {cert.rest_days > 0 ? (
                        <span className="badge badge-info">{cert.rest_days} {print_lang('days', 'days')}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleView(cert)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title={print_lang('view_print', 'View & Print')}
                        >
                          <Printer size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(cert)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title={print_lang('edit', 'Edit')}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(cert.cert_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title={print_lang('delete', 'Delete')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalCount > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                print_lang={print_lang}
              />
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Certificate Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileCheck size={24} />
                {modalMode === 'edit'
                  ? print_lang('edit_certificate', 'Edit Certificate')
                  : print_lang('new_certificate', 'New Medical Certificate')
                }
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Template Selection - only show for new */}
              {modalMode === 'add' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {print_lang('template', 'Template')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {templateTypes.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, template_type: template.id })}
                        className={`p-3 border rounded-lg text-left ${
                          formData.template_type === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium">{template.name}</p>
                        <p className="text-sm text-gray-500">{template.name_en}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Patient & Dentist Selection - disable for edit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('patient', 'Patient')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.patient_id}
                    onChange={(e) => handlePatientChange(e.target.value)}
                    required
                    disabled={modalMode === 'edit'}
                    className={modalMode === 'edit' ? 'bg-gray-100' : ''}
                  >
                    <option value="">{print_lang('select_patient', 'Select Patient')}</option>
                    {patients.map((patient) => (
                      <option key={patient.patient_id} value={patient.patient_id}>
                        {patient.prefix} {patient.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('dentist', 'Dentist')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.dentist_id}
                    onChange={(e) => setFormData({ ...formData, dentist_id: e.target.value })}
                    required
                    disabled={modalMode === 'edit'}
                    className={modalMode === 'edit' ? 'bg-gray-100' : ''}
                  >
                    <option value="">{print_lang('select_dentist', 'Select Dentist')}</option>
                    {dentists.map((dentist) => (
                      <option key={dentist.dentist_id} value={dentist.dentist_id}>
                        {dentist.name} {dentist.license_number ? `(${dentist.license_number})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Treatment Record (if patient selected) - only for new */}
              {modalMode === 'add' && formData.patient_id && treatmentRecords.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('link_treatment', 'Link to Treatment Record')}
                  </label>
                  <select
                    value={formData.treatment_record_id}
                    onChange={(e) => handleTreatmentSelect(e.target.value)}
                  >
                    <option value="">{print_lang('select_optional', 'Select (Optional)')}</option>
                    {treatmentRecords.map((tr) => (
                      <option key={tr.tmr_id} value={tr.tmr_id}>
                        {formatDate(tr.date)} - {tr.treatment_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Diagnosis */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {print_lang('diagnosis', 'Diagnosis')}
                </label>
                <textarea
                  value={formData.diagnosis}
                  onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                  rows={3}
                  placeholder={print_lang('enter_diagnosis', 'Enter diagnosis...')}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {commonDiagnoses.map((diag, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        diagnosis: formData.diagnosis
                          ? `${formData.diagnosis}\n${diag}`
                          : diag
                      })}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      {diag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recommendation */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {print_lang('recommendation', 'Recommendation')}
                </label>
                <textarea
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  rows={3}
                  placeholder={print_lang('enter_recommendation', 'Enter recommendation...')}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {commonRecommendations.map((rec, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        recommendation: formData.recommendation
                          ? `${formData.recommendation}\n${rec}`
                          : rec
                      })}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      {rec}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rest Days */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">{print_lang('rest_period', 'Rest Period')}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('rest_days', 'Rest Days')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={formData.rest_days}
                      onChange={(e) => handleRestDaysChange(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('from_date', 'From Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.rest_start_date}
                      onChange={(e) => setFormData({ ...formData, rest_start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('to_date', 'To Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.rest_end_date}
                      onChange={(e) => setFormData({ ...formData, rest_end_date: e.target.value })}
                    />
                  </div>
                </div>
                {/* Quick buttons */}
                <div className="mt-2 flex gap-2">
                  {[0, 1, 2, 3, 5, 7].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => handleRestDaysChange(days)}
                      className={`px-3 py-1 rounded text-sm ${
                        parseInt(formData.rest_days) === days
                          ? 'bg-blue-500 text-white'
                          : 'bg-white border hover:bg-gray-50'
                      }`}
                    >
                      {days === 0 ? print_lang('none', 'None') : `${days} ${print_lang('days', 'days')}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Issue Date - only for new */}
              {modalMode === 'add' && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('issue_date', 'Issue Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    required
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Save size={18} />
                  {isSaving
                    ? print_lang('saving', 'Saving...')
                    : modalMode === 'edit'
                      ? print_lang('save_changes', 'Save Changes')
                      : print_lang('create_certificate', 'Create Certificate')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintModal && selectedCert && (
        <div className="modal-overlay" onClick={() => setShowPrintModal(false)}>
          <div className="modal-content w-full max-w-4xl p-0 max-h-[95vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Toolbar */}
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FileText size={20} />
                {selectedCert.cert_number}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowPrintModal(false);
                    handleEdit(selectedCert);
                  }}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Edit size={18} />
                  {print_lang('edit', 'Edit')}
                </button>
                <button
                  onClick={handlePrint}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Printer size={18} />
                  {print_lang('print', 'Print')}
                </button>
                <button onClick={() => setShowPrintModal(false)} className="p-2 hover:bg-gray-200 rounded">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Nothing printed — red, because this needs acting on. */}
            {printError && (
              <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
                {printError}
              </div>
            )}
            {/* It printed, just not silently. */}
            {printWarning && (
              <div className="bg-yellow-50 border-b border-yellow-200 text-yellow-800 px-4 py-2 text-sm">
                {printWarning}
              </div>
            )}

            {/* Certificate Preview */}
            <div className="overflow-auto p-4 bg-gray-200" style={{ maxHeight: 'calc(95vh - 80px)' }}>
              <div className="bg-white shadow-xl mx-auto" style={{ width: '210mm', height: '297mm', transform: 'scale(0.85)', transformOrigin: 'top center' }}>
                {renderCertificateContent()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalCertificates;
